import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { byDesc, groupBy, parseDate, sortByComparisons } from "@lyfegen-platform/utils";
import { Box, Typography, useTheme } from "@material-ui/core";
import { addDays } from "date-fns";
import { useFieldValue, useSetFieldValue } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { AgreementAllocation } from "../components/flex-agreement/AgreementAllocation";
import { FlexCoverageDecisionMessage } from "../components/flexible/FlexCoverageDecisionMessage";
import { SectionHeader } from "../components/flexible/SectionHeader";
import useStyles from "../containers/data/forms/forms-styles";
import { useDataQuery } from "../hooks/useDataModule";
import { CADataPoints } from "../models/flexible/ca-data-points";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { FlexSectionSchema } from "../models/flexible/flex-section-schema";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import {
	AgreementScope,
	BaseAgreement,
	Case,
	CoverageRequestTherapyDecision,
	Disease,
	EnrollingConditionSet,
	EnrollingConditionType,
	FlexFormStatus,
	Maybe,
} from "../models/generated/graphql";
import { flexibleFormRelatedAgreements, flexibleFormValuesState, pageLoadingState } from "../store/flexible";
import { ConditionType, FlexFormStatus as RecordStatus } from "../types/graphql-global-types";
import { combineIgnoredAndMissingAttributes, getPreparationsWithDistinguishBrands, isConditionSetMatched } from "../utils/agreement-utils";
import { localeDate } from "../utils/locale-date";
import { maxBy } from "../utils/max-by";
import { minBy } from "../utils/min-by";
import { getCoverageBoundaries } from "../utils/negotiation-rounds";
import { neo4jDateToDate } from "../utils/neo4j-date-to-date";
import { GET_BRAND_BY_QUEST, GET_MATCHED_AGREEMENT_BY_CPDI, GET_MATCHED_AGREEMENT_BY_SCOPEID } from "./queries";
import { GetAgreementPropsByCaseId } from "./types/GetAgreementPropsByCaseId";
import { GetBrandByQuest, GetBrandByQuest_quests } from "./types/GetBrandByQuest";
import { GetMatchedAgreement } from "./types/GetMatchedAgreement";

function CoverageStartDateAfterTrialBanner(props: {
	cases: Array<Omit<Case, "flexEntities"> & { flexEntities: Array<TypedFlexEntity<any>> }>;
}) {
	const { palette } = useTheme();
	const { t } = useTranslation();
	const classes = useStyles();
	const [value] = useFieldValue<Date | string>(AttributeIdentifiers.CoverageStartDate);

	const coverageBoundaries = props.cases.flatMap(getCoverageBoundaries);
	const trialCoverageRequests = coverageBoundaries.filter((data) => data.decision === CoverageRequestTherapyDecision.Trial);
	const hasCoverageRequestApproved = coverageBoundaries.some((data) => data.decision === CoverageRequestTherapyDecision.Approved);

	const trialsWithGap =
		value &&
		trialCoverageRequests
			.filter((data) => data.endDate && data.endDate < addDays(neo4jDateToDate(value)!, -1))
			.sort(sortByComparisons(byDesc((data) => data.endDate)));

	return trialsWithGap?.length && !hasCoverageRequestApproved && trialsWithGap[0].endDate ? (
		<Box width="100%">
			<Typography variant="body1" className={classes.disclaimer} style={{ backgroundColor: palette.inputTableDivider.main }}>
				{t("therapy-trial-prolonged", {
					X: localeDate(trialsWithGap[0].endDate),
					Y: localeDate(addDays(parseDate(value), -1)),
				})}
			</Typography>
		</Box>
	) : null;
}

const getQuery = (disease: Disease | undefined, ignoredAttributes: Array<AttributeIdentifiers>) => {
	return disease || ignoredAttributes.includes(AttributeIdentifiers.Disease)
		? GET_MATCHED_AGREEMENT_BY_CPDI
		: GET_MATCHED_AGREEMENT_BY_SCOPEID;
};

function FetchRelatedAgreementsEffect(props: {
	caseIds?: Array<string>;
	questId?: string;
	patientId?: string;
	isEditable: boolean;
	isEditSubmitted: boolean;
	entityTypeId: string;
	isNegotiable?: boolean;
	ignoredAttributes: Array<AttributeIdentifiers>;
}) {
	const formData: CADataPoints = useRecoilValue(flexibleFormValuesState);
	const setRelatedAgreementsState = useSetRecoilState(flexibleFormRelatedAgreements);
	const setLoading = useSetRecoilState(pageLoadingState);
	const setFieldValue = useSetFieldValue();

	useEffect(() => setLoading(true), [setLoading]);
	const { data, loading, error } = useDataQuery<GetAgreementPropsByCaseId | GetMatchedAgreement>(
		getQuery(formData.disease, props.ignoredAttributes),
		{
			variables: {
				scopeId: formData.scopeId,
				caseIds: props.caseIds,
				icd10Code: formData.disease?.Icd10Code,
				indicationCode: formData.indication?.code,
				preparationBrandNames: getPreparationsWithDistinguishBrands(formData)?.map((p) => p.brandName.Name),
				genericNames: getPreparationsWithDistinguishBrands(formData)?.map((p) => p.genericName.Name),
				flexEntityTypeId: props.entityTypeId,
			},
			fetchPolicy: "no-cache",
			skip: !Object.keys(formData).length,
			onCompleted: () => {
				setLoading(false);
			},
		}
	);

	const { data: questData } = useDataQuery<GetBrandByQuest>(GET_BRAND_BY_QUEST, {
		variables: { id: props.questId },
		skip: !props.questId,
	});

	useEffect(() => {
		if (loading || !data) {
			return;
		}

		const quest = questData?.quests[0] as GetBrandByQuest_quests;
		const baseAgreements = (data as GetMatchedAgreement)?.baseAgreements;
		const groupedBasedAgreements = groupBy(
			(baseAgreements as unknown as Array<BaseAgreement>) || [],
			(baseAgreement) => baseAgreement.initialVersionId
		);

		const agreementMatchesWithAllVersions = Array.from(groupedBasedAgreements)
			.map(([, agreements]) => {
				const matches = agreements
					.filter((agreement) => agreement.allowedForms?.includes(props.entityTypeId))
					.flatMap((agreement) =>
						agreement.enrollingConditionSets.flatMap((conditionSet) => {
							return conditionSet.scopesConnection.edges
								.filter(
									(scope) =>
										!scope.excludedOn &&
										isConditionSetMatched({
											values: formData,
											agreementScope: scope.node,
											conditionSet,
											ignoredAttributes: props.ignoredAttributes,
										})
								)
								.map((scope) => ({
									scope: scope.node,
									conditionSet,
									agreement,
								}));
						})
					);
				return { agreements, matches };
			})
			.filter(({ matches }) => matches.length);
		const matchedAgreementScopes = agreementMatchesWithAllVersions.map(({ matches }) =>
			maxBy(matches, ({ agreement }) => agreement.pushedOn)
		);
		const ratingConditions = matchedAgreementScopes.flatMap(({ conditionSet }) =>
			conditionSet.conditions.filter((condition) => condition.conditionType === EnrollingConditionType.Rating)
		);
		const singleRatingCondition = ratingConditions.find((condition) => condition.rating?.length === 1);
		if (singleRatingCondition) {
			setFieldValue(AttributeIdentifiers.Rating, singleRatingCondition.rating?.[0]);
		}

		if (props.isEditable && !formData.decisionTherapy) {
			const decisionCondition = matchedAgreementScopes
				.flatMap(({ conditionSet }) =>
					conditionSet.conditions.filter((condition) => condition.conditionType === EnrollingConditionType.TherapyDecision)
				)
				.find((condition) => condition.therapyDecision?.length === 1);
			if (decisionCondition && !props.questId) {
				setFieldValue(AttributeIdentifiers.DecisionTherapy, decisionCondition.therapyDecision?.[0]);
			}

			if (props.questId) {
				setFieldValue(AttributeIdentifiers.DecisionTherapy, CoverageRequestTherapyDecision.Approved);
			}
		}

		const isRatingCondition = matchedAgreementScopes?.some(
			(agreementScope: { scope: AgreementScope; conditionSet: EnrollingConditionSet; agreement: BaseAgreement }) =>
				agreementScope.agreement.scenarios
					.flatMap((scenario: any) => scenario.conditions)
					.some((s: any) => s?.conditionType === ConditionType.RATING) ||
				agreementScope.agreement.enrollingConditionSets.some((enrollingConditionSet: EnrollingConditionSet) =>
					enrollingConditionSet.conditions.some((condition) => condition.conditionType === EnrollingConditionType.Rating)
				)
		);

		if (quest) {
			const records = quest.recordsRelationship.filter(
				(record) => record.status !== RecordStatus.REVOKED && props.patientId === record.insuranceId
			);
			const earliestAdministrationDate = minBy(
				records.map((record) => record?.administrationDate),
				(x) => x
			);
			setFieldValue(AttributeIdentifiers.CoverageStartDate, earliestAdministrationDate);
		}

		if (!props.isNegotiable) {
			setFieldValue("isRatingCondition", isRatingCondition);
		}

		setRelatedAgreementsState({
			data: matchedAgreementScopes,
			allMatchedAgreementVersions: agreementMatchesWithAllVersions.flatMap(({ agreements }) => agreements),
			loading,
			error,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, loading, error, setRelatedAgreementsState, setFieldValue, props.isEditable]);

	return null;
}

export class CoverageDecisionSectionController implements FlexSectionController {
	context: FlexContext = {} as any;

	constructor(
		readonly schema: FlexSectionSchema,
		readonly path: string,
		readonly fieldControllers: Array<FlexFieldController>,
		readonly sectionControllers: Array<FlexSectionController>
	) {}

	renderBeforeHeader(): Maybe<React.ReactElement> {
		const casesIds = this.context.cases.map((c) => c.id);
		const questId = this.context.parameters.questId;
		const patientId = this.context.parameters.patientId;

		const renderFetchRelatedAgreements = () => {
			return (
				<FetchRelatedAgreementsEffect
					caseIds={casesIds.length > 0 ? casesIds : undefined}
					questId={questId}
					patientId={patientId}
					isEditable={this.context.isEditable}
					isEditSubmitted={this.context.isEditable && this.context.flexEntity?.status === FlexFormStatus.Submitted}
					entityTypeId={this.context.entityType.id}
					ignoredAttributes={combineIgnoredAndMissingAttributes(
						this.context.entityType.ignoredAttributes,
						this.context.entityType.catalogueAttributes || []
					)}
					isNegotiable={this.context.isNegotiable}
				/>
			);
		};

		if (this.context.isNegotiable) {
			return renderFetchRelatedAgreements();
		}

		return (
			<Box mb={2}>
				<SectionHeader localizableKey={"allocation-to-agreement"} />
				{renderFetchRelatedAgreements()}
				<AgreementAllocation />
			</Box>
		);
	}

	renderBeforeContent(): Maybe<React.ReactElement> {
		return this.context.isEditable ? <CoverageStartDateAfterTrialBanner cases={this.context.cases} /> : null;
	}

	renderAfterHeader(): Maybe<React.ReactElement> {
		return !this.context.flexEntity ? (
			<Box mb={1}>
				<FlexCoverageDecisionMessage />
			</Box>
		) : null;
	}
}
